import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { GET_TAG_TYPES, CREATE_TAG_TYPE, DELETE_TAG_TYPE, CREATE_TAG, DELETE_TAG, ADD_TAG_TO_EMAIL_TEMPLATE, REMOVE_TAG_FROM_EMAIL_TEMPLATE, ADD_TAG_TO_LANDING_PAGE, REMOVE_TAG_FROM_LANDING_PAGE, UPDATE_TAG_TYPE, UPDATE_TAG } from '../components/Queries/Tags'
import { LoadingBlock, ErrorAlerts } from '../components/common'
import { Table, Button, Select, Modal, Input, Tabs, message } from 'antd'
import _snakeCase from 'lodash/snakeCase'
import gql from 'graphql-tag'

const GET_SIMULATION_EMAIL_TEMPLATES = gql`
  query {
    simulationEmailTemplates (restrictToOwn: true) {
      id
      name
      locales
      companyId
      company {
        id
        name
      }
      tags {
        id
        label
        tagType {
          id
          label
        }
      }
    }
  }
`

const GET_SIMULATION_LANDING_PAGE_TEMPLATES = gql`
  query {
    simulationLandingPages (restrictToOwn: true) {
      id
      name
      locales
      companyId
      company {
        id
        name
      }
      tags {
        id
        label
        tagType {
          id
          label
        }
      }
    }
  }
`

export function TaggingDashboard () {
  return (
    <div style={{ margin: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
      <h1 style={{ margin: 0, padding: 24, paddingBottom: 16, background: 'white' }}>Tagging Dashboard</h1>

      <Tabs defaultActiveKey='tags'>
        <Tabs.TabPane tab='Tags' key='tags' style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <ManageTagTypes />
          <ManageTags />
        </Tabs.TabPane>
        <Tabs.TabPane tab='Email Templates' key='email'>
          <EmailTemplates />
        </Tabs.TabPane>
        <Tabs.TabPane tab='Landing Pages' key='landing'>
          <LandingPages />
        </Tabs.TabPane>
      </Tabs>
    </div>
  )
}

function ManageTagTypes () {
  const getTagsQuery = useQuery(GET_TAG_TYPES)

  const [deleteTagType] = useMutation(DELETE_TAG_TYPE, {
    refetchQueries: [
      { query: GET_TAG_TYPES },
      { query: GET_SIMULATION_EMAIL_TEMPLATES },
      { query: GET_SIMULATION_LANDING_PAGE_TEMPLATES }
    ],
    onError: (error) => message.error(error.message)
  })

  const [renameTagTypeModal, setRenameTagTypeModal] = useState(null)

  const [addTagModal, setAddTagModal] = useState(null)
  const [showAddTypeModal, setShowAddTypeModal] = useState(false)

  if (getTagsQuery.loading) return <LoadingBlock loading />
  if (getTagsQuery.error) return <ErrorAlerts error={getTagsQuery.error} />

  return (
    <div style={{ padding: 24, background: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Tag Types ({getTagsQuery.data.tagTypes.length})</h2>
        <Button type='primary' size='small' onClick={() => setShowAddTypeModal(true)}>
          Add Tag Type
        </Button>
      </div>

      <Table
        dataSource={getTagsQuery.data.tagTypes.map(tt => ({
          key: tt.id,
          id: tt.id,
          label: tt.label,
          slug: tt.slug,
          tagCount: tt.tags.length
        }))}
        columns={[
          { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
          { title: 'Label', dataIndex: 'label', key: 'label' },
          { title: 'Slug', dataIndex: 'slug', key: 'slug' },
          { title: 'Tag Count', dataIndex: 'tagCount', key: 'tagCount', align: 'center', width: 110 },
          {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button
                  size='small'
                  onClick={() => setAddTagModal(record)}
                >
                  Add Tag
                </Button>
                <Button
                  size='small'
                  onClick={() => setRenameTagTypeModal(record)}
                >
                  Rename
                </Button>
                <Button
                  type='danger'
                  size='small'
                  onClick={() => window.confirm(`Are you sure you want to delete the tag type "${record.label}" with ${record.tagCount} tags and all associations?`) && deleteTagType({ variables: { id: record.id } })}
                >
                  Delete
                </Button>
              </div>
            )
          }
        ]}
        pagination={false}
        style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #ccc' }}
      />

      {addTagModal && (
        <AddTagModal
          tagType={addTagModal}
          handleClose={() => setAddTagModal(null)}
        />
      )}

      {renameTagTypeModal && (
        <RenameTagTypeModal
          tagType={renameTagTypeModal}
          handleClose={() => setRenameTagTypeModal(null)}
        />
      )}

      {showAddTypeModal && (
        <AddTagTypeModal handleClose={() => setShowAddTypeModal(false)} />
      )}
    </div>
  )
}

function ManageTags () {
  const getTagsQuery = useQuery(GET_TAG_TYPES)
  const getEmailTemplatesQuery = useQuery(GET_SIMULATION_EMAIL_TEMPLATES)
  const getLandingPagesQuery = useQuery(GET_SIMULATION_LANDING_PAGE_TEMPLATES)

  const [deleteTag] = useMutation(DELETE_TAG, {
    refetchQueries: [
      { query: GET_TAG_TYPES },
      { query: GET_SIMULATION_EMAIL_TEMPLATES },
      { query: GET_SIMULATION_LANDING_PAGE_TEMPLATES }
    ],
    onError: (error) => message.error(error.message)
  })

  const [renameTagModal, setRenameTagModal] = useState(null)

  if (getTagsQuery.loading || getEmailTemplatesQuery.loading || getLandingPagesQuery.loading) return <LoadingBlock loading />
  if (getTagsQuery.error || getEmailTemplatesQuery.error || getLandingPagesQuery.error) return <ErrorAlerts error={getTagsQuery.error || getEmailTemplatesQuery.error || getLandingPagesQuery.error} />

  return (
    <div style={{ padding: 24, background: 'white' }}>
      <h2>Tags ({getTagsQuery.data.tagTypes.reduce((acc, tagType) => acc + tagType.tags.length, 0)})</h2>

      <Table
        dataSource={getTagsQuery.data.tagTypes.flatMap(tagType => tagType.tags.map(tag => ({
          key: tag.id,
          id: tag.id,
          tagType: tagType.label,
          label: tag.label,
          slug: tag.slug,
          associations: getEmailTemplatesQuery.data.simulationEmailTemplates.filter(template => template.tags.some(t => t.id === tag.id)).length + getLandingPagesQuery.data.simulationLandingPages.filter(page => page.tags.some(t => t.id === tag.id)).length
        })))}
        columns={[
          { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
          { title: 'Tag Type', dataIndex: 'tagType', key: 'tagType' },
          { title: 'Label', dataIndex: 'label', key: 'label' },
          { title: 'Slug', dataIndex: 'slug', key: 'slug' },
          { title: 'Associations', dataIndex: 'associations', key: 'associations', align: 'center', width: 130 },
          {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button
                  size='small'
                  onClick={() => setRenameTagModal(record)}
                >
                  Rename
                </Button>
                <Button
                  type='danger'
                  size='small'
                  onClick={() => window.confirm(`Are you sure you want to delete the tag "${record.label}" and all associations?`) && deleteTag({ variables: { id: record.id } })}
                >
                  Delete
                </Button>
              </div>
            )
          }
        ]}
        pagination={false}
        style={{ marginTop: 24, borderTop: '1px solid #ccc' }}
      />

      {renameTagModal && (
        <RenameTagModal
          tag={renameTagModal}
          handleClose={() => setRenameTagModal(null)}
        />
      )}
    </div>
  )
}

function EmailTemplates () {
  const { loading, error, data } = useQuery(GET_SIMULATION_EMAIL_TEMPLATES)
  const [search, setSearch] = useState('')

  const [addTag] = useMutation(ADD_TAG_TO_EMAIL_TEMPLATE, {
    onError: (error) => message.error(error.message)
  })
  const [removeTag] = useMutation(REMOVE_TAG_FROM_EMAIL_TEMPLATE, {
    onError: (error) => message.error(error.message)
  })

  if (loading) return <LoadingBlock fullScreen loading />
  if (error) return <ErrorAlerts error={error} />

  const emailTemplates = data.simulationEmailTemplates
    .filter(template => template.companyId === null || template.company.name === 'usecure')

  const filteredTemplates = emailTemplates.filter(template =>
    template.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div style={{ padding: 24, background: 'white' }}>
      <h2>Email Templates ({emailTemplates.length}) {filteredTemplates.length < emailTemplates.length && <span>({filteredTemplates.length} filtered)</span>}</h2>
      <Input.Search
        placeholder='Search title...'
        allowClear
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 12, width: 300 }}
      />
      <TaggableListing
        items={filteredTemplates.map(({ id, name, tags }) => ({ id, title: name, tags, link: `/phish/email-templates/view/${id}` }))}
        handleAddTag={(id, tagId) => addTag({ variables: { id, tagId } })}
        handleRemoveTag={(id, tagId) => removeTag({ variables: { id, tagId } })}
      />
    </div>
  )
}

function LandingPages () {
  const { loading, error, data } = useQuery(GET_SIMULATION_LANDING_PAGE_TEMPLATES)
  const [search, setSearch] = useState('')

  const [addTag] = useMutation(ADD_TAG_TO_LANDING_PAGE, {
    onError: (error) => message.error(error.message)
  })
  const [removeTag] = useMutation(REMOVE_TAG_FROM_LANDING_PAGE, {
    onError: (error) => message.error(error.message)
  })

  if (loading) return <LoadingBlock fullScreen loading />
  if (error) return <ErrorAlerts error={error} />

  const landingPages = data.simulationLandingPages
    .filter(page => page.companyId === null || page.company.name === 'usecure')

  const filteredPages = landingPages.filter(page =>
    page.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div style={{ padding: 24, background: 'white' }}>
      <h2>Landing Pages ({landingPages.length}) {filteredPages.length < landingPages.length && <span>({filteredPages.length} filtered)</span>}</h2>
      <Input.Search
        placeholder='Search title...'
        allowClear
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 12, width: 300 }}
      />
      <TaggableListing
        items={filteredPages.map(({ id, name, tags }) => ({ id, title: name, tags, link: `/phish/landing-pages/view/${id}` }))}
        handleAddTag={(id, tagId) => addTag({ variables: { id, tagId } })}
        handleRemoveTag={(id, tagId) => removeTag({ variables: { id, tagId } })}
      />
    </div>
  )
}

function TaggableListing ({ items, handleAddTag, handleRemoveTag }) {
  const { loading, error, data } = useQuery(GET_TAG_TYPES)

  const dataSource = items.map(item => ({ key: item.id, ...item }))

  const columns = [
    {
      width: 700,
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>{text}</span>
          <a href={record.link} target='_blank' rel='noopener noreferrer'>View</a>
        </div>
      )
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags, record) => {
        if (loading) return 'Loading...'
        if (error) return 'Error'

        const associatedIds = tags
          .slice()
          .sort((a, b) => (a.tagType.label + a.label).localeCompare(b.tagType.label + b.label))
          .map(t => t.id)

        const handleChange = (selectedIds) => {
          associatedIds.forEach(id => {
            if (!selectedIds.includes(id)) handleRemoveTag(record.id, id)
          })
          selectedIds.forEach(id => {
            if (!associatedIds.includes(id)) handleAddTag(record.id, id)
          })
        }

        return (
          <Select
            mode='multiple'
            style={{ width: '100%' }}
            value={associatedIds}
            onChange={handleChange}
            filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          >
            {data.tagTypes
              .slice()
              .flatMap(tagType => tagType.tags.map(tag => ({ tagType: tagType.label, tag: tag })))
              .sort((a, b) => a.tag.label.localeCompare(b.tag.label))
              .map(tag => (
                <Select.Option key={tag.tag.id} value={tag.tag.id}>{`${tag.tagType} : ${tag.tag.label}`}</Select.Option>
              ))}

          </Select>
        )
      }
    }
  ]

  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      pagination={{ pageSize: 20 }}
    />
  )
}

function AddTagModal ({ tagType, handleClose }) {
  const [label, setLabel] = useState('')
  const [slug, setSlug] = useState('')

  const [createTag, { loading }] = useMutation(CREATE_TAG, {
    refetchQueries: [{ query: GET_TAG_TYPES }],
    onCompleted: () => {
      handleClose()
    },
    onError: (error) => message.error(error.message)
  })

  const handleOk = () => {
    const trimmedLabel = label.trim()
    const trimmedSlug = slug.trim()

    if (trimmedLabel.length === 0) return

    createTag({ variables: { tagTypeId: tagType.id, label: trimmedLabel, slug: trimmedSlug } })
  }

  return (
    <Modal
      visible
      title={`Add Tag to "${tagType.label}"`}
      onOk={handleOk}
      onCancel={handleClose}
      okText='Add Tag'
      confirmLoading={loading}
    >
      <Input
        placeholder='Label'
        value={label}
        onChange={e => {
          setLabel(e.target.value)
          setSlug(_snakeCase(e.target.value))
        }}
        style={{ marginBottom: 8 }}
      />
      <Input
        placeholder='Slug'
        value={slug}
        onChange={e => setSlug(e.target.value)}
      />
    </Modal>
  )
}

function AddTagTypeModal ({ handleClose }) {
  const [label, setLabel] = useState('')
  const [slug, setSlug] = useState('')

  const [createTagType, { loading }] = useMutation(CREATE_TAG_TYPE, {
    refetchQueries: [{ query: GET_TAG_TYPES }],
    onCompleted: () => {
      handleClose()
    },
    onError: (error) => message.error(error.message)
  })

  const handleOk = () => {
    const trimmedLabel = label.trim()
    const trimmedSlug = slug.trim()

    if (trimmedLabel.length === 0) return

    createTagType({ variables: { label: trimmedLabel, slug: trimmedSlug } })
  }

  return (
    <Modal
      visible
      title='Add Tag Type'
      onOk={handleOk}
      onCancel={handleClose}
      okText='Add Tag Type'
      confirmLoading={loading}
    >
      <Input
        placeholder='Label'
        value={label}
        onChange={e => {
          setLabel(e.target.value)
          setSlug(_snakeCase(e.target.value))
        }}
        style={{ marginBottom: 8 }}
      />
      <Input
        placeholder='Slug'
        value={slug}
        onChange={e => setSlug(e.target.value)}
      />
    </Modal>
  )
}

function RenameTagTypeModal ({ tagType, handleClose }) {
  const [label, setLabel] = useState(tagType.label)
  const [slug, setSlug] = useState(tagType.slug)

  const [updateTagType, { loading }] = useMutation(UPDATE_TAG_TYPE, {
    refetchQueries: [{ query: GET_TAG_TYPES }],
    onCompleted: () => {
      handleClose()
    },
    onError: (error) => message.error(error.message)
  })

  const handleOk = () => {
    const trimmedLabel = label.trim()
    const trimmedSlug = slug.trim()

    if (trimmedLabel.length === 0) return

    updateTagType({ variables: { id: tagType.id, label: trimmedLabel, slug: trimmedSlug } })
  }

  return (
    <Modal
      visible
      title='Rename Tag Type'
      onOk={handleOk}
      onCancel={handleClose}
      okText='Save Changes'
      confirmLoading={loading}
    >
      <Input
        placeholder='Label'
        value={label}
        onChange={e => {
          setLabel(e.target.value)
          setSlug(_snakeCase(e.target.value))
        }}
        style={{ marginBottom: 8 }}
      />
      <Input
        placeholder='Slug'
        value={slug}
        onChange={e => setSlug(e.target.value)}
      />
    </Modal>
  )
}

function RenameTagModal ({ tag, handleClose }) {
  const [label, setLabel] = useState(tag.label)
  const [slug, setSlug] = useState(tag.slug)

  const [updateTag, { loading }] = useMutation(UPDATE_TAG, {
    refetchQueries: [
      { query: GET_TAG_TYPES },
      { query: GET_SIMULATION_EMAIL_TEMPLATES },
      { query: GET_SIMULATION_LANDING_PAGE_TEMPLATES }
    ],
    onCompleted: () => {
      handleClose()
    },
    onError: (error) => message.error(error.message)
  })

  const handleOk = () => {
    const trimmedLabel = label.trim()
    const trimmedSlug = slug.trim()

    if (trimmedLabel.length === 0) return

    updateTag({ variables: { id: tag.id, label: trimmedLabel, slug: trimmedSlug } })
  }

  return (
    <Modal
      visible
      title='Rename Tag'
      onOk={handleOk}
      onCancel={handleClose}
      okText='Save Changes'
      confirmLoading={loading}
    >
      <Input
        placeholder='Label'
        value={label}
        onChange={e => {
          setLabel(e.target.value)
          setSlug(_snakeCase(e.target.value))
        }}
        style={{ marginBottom: 8 }}
      />
      <Input
        placeholder='Slug'
        value={slug}
        onChange={e => setSlug(e.target.value)}
      />
    </Modal>
  )
}
